import { Pencil, MapPin } from 'lucide-react';
import React, { useState, lazy, Suspense } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/card';
import EtapeList from './EtapeList';
import EtapeModal from './modals/EtapeModal';
import { ActionBar } from '@/components/ui/ActionBar';
import { SpinnerOverlay } from '@/components/ui/Spinner';

const LeafletMap = lazy(() => import('../../project/components/modals/LeafletMap'));

const DataRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="grid grid-cols-[180px_1fr] gap-4 py-2 border-b border-neutral-100 last:border-0 hover:bg-neutral-25/50 -mx-2 px-2 rounded transition-colors">
    <div className="text-sm text-neutral-500">{label}</div>
    <div className="text-sm text-neutral-900">{value}</div>
  </div>
);

const Section = ({
  title,
  badge,
  onEdit,
  children,
}: {
  title: string;
  badge?: string;
  onEdit?: () => void;
  children: React.ReactNode;
}) => (
  <div className="border-b border-neutral-150 py-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-neutral-900 tracking-tight">{title}</h3>
      <div className="flex items-center gap-2">
        {badge && (
          <Badge variant="outline" size="sm">
            {badge}
          </Badge>
        )}
        {onEdit && (
          <Button
            variant="ghost"
            size="icon-sm"
            data-testid={`building-detail-section-edit-btn-${title}`}
            onClick={onEdit}
          >
            <Pencil size={12} />
          </Button>
        )}
      </div>
    </div>
    {children}
  </div>
);

export default function BuildingDetailView({
  data,
  makeEditable,
  updateEtape,
  permission,
  updateDetail,
}) {
  const [visible, setVisible] = useState(false);
  const [currentEtape, setCurrentEtape] = useState(null);

  const getEtapeDetail = (id) => {
    const tmp = (data?.etape_list || []).find((ele) => ele.id === id);
    if (tmp) {
      setCurrentEtape(tmp);
    }
    setVisible(true);
  };

  const handleEditEtape = (id) => {
    return (
      <Pencil
        className="h-4 w-4 cursor-pointer text-neutral-500 hover:text-neutral-900"
        data-testid={`building-etape-edit-${id}`}
        onClick={() => getEtapeDetail(id)}
      />
    );
  };

  const submitEditedValues = async (values) => {
    try {
      await updateEtape(currentEtape.id, values);
      setVisible(false);
    } catch (err) {
      console.error(err);
    }
  };

  const canEdit = permission === 2 && data;
  const identity = data?.identity_data || {};
  const etapeCount = data?.etape_list?.length;

  return (
    <div className="flex-1 overflow-y-auto">
      <ActionBar
        left={<h3 className="text-sm font-semibold tracking-tight">Biblio</h3>}
        right={
          <Button
            variant="ghost"
            size="icon-sm"
            data-testid="building-detail-edit-btn"
            onClick={makeEditable}
          >
            <Pencil size={12} />
          </Button>
        }
      />
      {data?.id && (
        <div className="px-6 py-6">
          <Section
            title="Adresní místo stavby"
            badge={`${
              [
                identity.locality,
                identity.sub_locality,
                identity.street,
                identity.postal_code,
              ].filter(Boolean).length
            } položky`}
            onEdit={canEdit ? makeEditable : undefined}
          >
            <DataRow label="Obec" value={identity.locality || ''} />
            <DataRow label="Část obce" value={identity.sub_locality || ''} />
            <DataRow label="Ulice" value={identity.street || ''} />
            <DataRow
              label="PSČ"
              value={<span className="font-mono">{identity.postal_code || ''}</span>}
            />
          </Section>

          <Section title="Definiční bod" onEdit={canEdit ? makeEditable : undefined}>
            {data.latitude && data.longitude ? (
              <>
                <Card className="overflow-hidden mb-3">
                  <CardContent className="p-0 h-[200px]">
                    <Suspense
                      fallback={
                        <div className="h-full flex items-center justify-center bg-neutral-25">
                          <SpinnerOverlay />
                        </div>
                      }
                    >
                      <LeafletMap
                        onMapClick={() => {}}
                        locations={[{ latitude: data.latitude, longitude: data.longitude }]}
                        style={{ height: '100%', width: '100%' }}
                      />
                    </Suspense>
                  </CardContent>
                </Card>
                <Badge variant="outline" size="lg" className="font-mono gap-1.5">
                  <MapPin size={12} className="text-primary-600" />
                  {data.latitude}° N, {data.longitude}° E
                </Badge>
              </>
            ) : (
              <p className="text-sm text-neutral-500">Definiční bod nebyl nastaven</p>
            )}
          </Section>

          <Section
            title="Životní etapy stavby"
            badge={etapeCount ? `${etapeCount} etap` : undefined}
            onEdit={canEdit ? makeEditable : undefined}
          >
            {data && <EtapeList data={data?.etape_list || []} actions={[handleEditEtape]} />}
          </Section>
        </div>
      )}

      <EtapeModal
        visible={visible}
        onClose={(withReset) => {
          if (withReset) {
            updateDetail();
          }
          setVisible(false);
        }}
        data={[]}
        submitForm={submitEditedValues}
        formProps={currentEtape}
        title="Etapa"
      />
    </div>
  );
}
